import { createAction, createReducer } from "@reduxjs/toolkit"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { toSearchParams } from "helpers/form"
import { numberToCurrency } from "helpers/string"
import { buildGuestTypes, buildGroupRow } from "pagesV2/pro/Bookings/CheckIn/checkInUtils"

dayjs.extend(customParseFormat)

// Projected auto-collect schedule = 24h before the experience start. Mirrors the old booking
// detail page (PaymentSummary), which derives this on the FE rather than from the backend, so
// the date is available even when auto-collect is currently off (the enable-confirmation modal
// needs it). booked_datetime is "MMMM DD, YYYY", start_time is a "h:mm A"-style clock string.
const buildCollectionSchedule = (bookedDatetime, startTime) => {
  if (!bookedDatetime || !startTime) return null
  const start = dayjs(`${bookedDatetime} ${startTime}`, ["MMMM D, YYYY h:mm A", "MMMM DD, YYYY h:mm A", "MMMM D, YYYY hh:mm A"])
  return start.isValid() ? start.subtract(24, "hours").format("MMM D, YYYY - hh:mm A") : null
}

const PAGINATION_INITIAL_STATE = {
  page: 1,
  next_page: null,
  prev_page: null,
  total_results: 0,
  total_pages: 1
}

// ACTIONS
const requested = createAction("reservations/requested")
const received = createAction("reservations/received")
const failed = createAction("reservations/failed")

const requestedDetail = createAction("reservations/detail/requested")
const receivedDetail = createAction("reservations/detail/received")
const failedDetail = createAction("reservations/detail/failed")

const requestedCheckIn = createAction("reservations/checkIn/requested")
const receivedCheckIn = createAction("reservations/checkIn/received")
const failedCheckIn = createAction("reservations/checkIn/failed")

// Lazy-loaded detail sub-resources, each fetched from its own endpoint
const requestedWaivers = createAction("reservations/waivers/requested")
const receivedWaivers = createAction("reservations/waivers/received")
const failedWaivers = createAction("reservations/waivers/failed")

const requestedPaymentHistory = createAction("reservations/paymentHistory/requested")
const receivedPaymentHistory = createAction("reservations/paymentHistory/received")
const failedPaymentHistory = createAction("reservations/paymentHistory/failed")

const requestedCommunications = createAction("reservations/communications/requested")
const receivedCommunications = createAction("reservations/communications/received")
const failedCommunications = createAction("reservations/communications/failed")

const requestedAnswers = createAction("reservations/answers/requested")
const receivedAnswers = createAction("reservations/answers/received")
const failedAnswers = createAction("reservations/answers/failed")

const requestedPaymentOption = createAction("reservations/paymentOption/requested")
const receivedPaymentOption = createAction("reservations/paymentOption/received")
const failedPaymentOption = createAction("reservations/paymentOption/failed")

const requestedChangeHistory = createAction("reservations/changeHistory/requested")
const receivedChangeHistory = createAction("reservations/changeHistory/received")
const failedChangeHistory = createAction("reservations/changeHistory/failed")

// MAPPER — backend snake_case → FE camelCase internal shape
export const mapReservationItem = (item) => {
  const isMultiDay = item.duration_type === "multi_day"
  const experienceTime = isMultiDay
    ? item.experience_time?.start_time || null
    : [item.experience_time?.start_time, item.experience_time?.end_time].filter(Boolean).join(" - ") || null

  return {
    id: item.uuid,
    bookingId: item.booking_id,
    name: item.full_name || "",
    phone: item.phone || null,
    email: item.email || null,
    // Client identity for the messaging modal (ClientContactForm sends by numeric user_id)
    userId: item.user_id ?? null,
    user: item.user || null,
    // Lifecycle status for the All Reservations sub-tabs (booked/requested/completed/canceled)
    status: item.status || null,
    experience: item.trip_title || "",
    experienceDate: item.experience_date || "",
    experienceTime: experienceTime,
    experienceIsGroup: item.experience_is_group ?? false,
    guestCount: item.guest_count || 0,
    checkInState: item.check_in_state || "pending",
    // Partial check-in/out: how many of total_guest_count are checked in / out
    checkedInCount: item.checked_in_count ?? 0,
    checkedOutCount: item.checked_out_count ?? 0,
    // Per-type breakdown for the check-in/out modals (falls back to a single "Guests" row)
    guestTypes: buildGuestTypes(item.guest_types, item.guest_count || 0, item.checked_in_count ?? 0, item.checked_out_count ?? 0),
    // Single group row for the check-in/out modal — the private booking is checked in/out
    // as one unit (client name, guest count, booking-level documents).
    guests: buildGroupRow({
      id: item.uuid,
      name: item.full_name,
      guestCount: item.guest_count || 0,
      state: item.check_in_state,
      checkedIn: item.checked_in_count ?? 0,
      checkedOut: item.checked_out_count ?? 0,
      docsSigned: item.docs_signed ?? 0,
      docsTotal: item.docs_total ?? 0,
      documents: item.documents
    }),
    // Required documents all signed → check-in without the incomplete-docs override
    waiversComplete: (item.docs_signed ?? 0) >= (item.docs_total ?? 0),
    paymentTag: item.payment_tag || { label: "Payment Failed", color: "red" },
    collectionTag: item.collection_tag || null,
    docsSign: item.docs_signed ?? 0,
    docsTotal: item.docs_total ?? 0
  }
}

const titleCase = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value)

// Change types whose values are bare decimal amounts ("1091.45") — the backend sends money
const MONEY_CHANGE_TYPES = new Set(["price_changed", "discount_changed"])

// Format a change value for display: money change types get a "$1,091.45" currency string;
// everything else (dates, guest counts, status labels, names) is rendered verbatim. Null is
// preserved — the panel uses it to detect added/removed (see renderChangeText).
const formatChangeValue = (changeType, value) => {
  if (value == null || value === "") return value ?? null
  if (MONEY_CHANGE_TYPES.has(changeType) && !Number.isNaN(Number(value))) return numberToCurrency(value)
  return value
}

// GET /guide/bookings/{id}/change-history — one change row (a single edited field).
// `old_value`/`new_value` are pre-formatted display strings; a null carries meaning the panel
// reads: null→value = added, value→null = removed, both null = a marker row (booking_created).
export const mapBookingHistoryChange = (change) => ({
  changeType: change.change_type ?? null,
  field: change.field_name ?? null,
  label: change.label ?? null,
  from: formatChangeValue(change.change_type, change.old_value),
  to: formatChangeValue(change.change_type, change.new_value),
  // Crew rows carry the assigned role (e.g. "Crew Member", "Business Owner"), shown in brackets
  // after the crew name — "Crew added: sanjeev Saini (Crew Member)".
  roleName: change.role_name ?? null,
  // Guide's own note from the adjust-price screen — only present on manual price adjustments.
  note: change.note ?? null
})

// Booking change-history entry — one entry = one edit (save), grouping one or more field
// changes under the actor + timestamp that made them. `changed_by` is an object (or null for
// changes recorded before this feature shipped), so render its `name`, not the object.
export const mapBookingHistoryEntry = (entry, index) => ({
  id: entry.id ?? `history-${index}`,
  changes: (Array.isArray(entry.changes) ? entry.changes : [])
    .map(mapBookingHistoryChange)
    // Keep event markers (a label with no field, e.g. "Booking Created") and any change that
    // actually has a value; drop field changes with no old/new value (e.g. an untouched "Notes"),
    // which would otherwise render as a meaningless bare label.
    .filter((change) => {
      const hasValue = change.from != null || change.to != null
      const isMarker = !change.field && !!change.label
      return hasValue || isMarker
    }),
  changeSource: entry.change_source ?? null,
  author: entry.changed_by?.name ?? null,
  // Date and time are bullet-separated to match the design's "author • date • time" meta line.
  dateTime: entry.changed_at ? dayjs(entry.changed_at).format("MM/DD/YYYY • hh:mm A") : ""
})

// MAPPER — backend reservation detail (flat booking) → ReservationDetailPage shape.
// The detail page components expect the nested shape below; the API returns a flat
// snake_case booking with client/trip/payment_summary/payment_history/booking_waivers.
export const mapReservationDetail = (payload) => {
  const item = payload?.data || payload
  if (!item) return null

  const client = item.client || {}
  const trip = item.trip || {}
  const paymentSummary = item.payment_summary || {}
  const waivers = Array.isArray(item.booking_waivers) ? item.booking_waivers : []
  const paymentHistory = Array.isArray(item.payment_history) ? item.payment_history : []
  // A private reservation is a single guest group; the check-in status + guest count live on it
  // (guest_groups[0].check_in_state / guest_count) rather than at the top level, so use it as the
  // source when the top-level fields aren't present.
  const guestGroup = Array.isArray(item.guest_groups) ? item.guest_groups[0] : null
  const checkInState = item.check_in_state ?? guestGroup?.check_in_state ?? null
  const checkedInCount = item.checked_in_count ?? guestGroup?.checked_in_count ?? 0
  const checkedOutCount = item.checked_out_count ?? guestGroup?.checked_out_count ?? 0
  const totalGuests =
    item.total_guest_count ??
    guestGroup?.guest_count ??
    (item.adult_count ?? 0) + (item.child_count ?? 0) + (item.infant_count ?? 0) + (item.passenger_count ?? 0)

  const clientName = [client.first_name, client.last_name].filter(Boolean).join(" ").trim()
  const signedWaiverCount = waivers.filter((waiver) => waiver.is_signed).length
  const remainingBalance = paymentSummary.remaining_balance ?? item.price_breakdown?.remaining_balance ?? 0
  const isPaidInFull = remainingBalance === 0

  // Prefer the same payment_tag / collection_tag the list endpoint returns so the
  // detail panel stays consistent with the listing/ActionQueue. Fall back to a basic
  // paid/balance-due tag until the backend includes them on the detail payload.
  const paymentTag = item.payment_tag || { label: isPaidInFull ? "Paid in Full" : "Balance Due", color: isPaidInFull ? "green" : "orange" }
  const collectionTag = item.collection_tag || null

  const hasDeposit = (item.deposit_amount ?? 0) > 0
  const isCashBalance = /cash/i.test(paymentTag.label || "")
  const depositLabel = (() => {
    if (isPaidInFull) return "Total payment collected"
    if (!hasDeposit) return "No deposit collected, balance due"
    return isCashBalance ? "Deposit paid & balance due in cash" : "Deposit paid & balance due to card"
  })()

  // Per-guest-type breakdown rows; only include a type when its count is > 0.
  // NOTE: payment_summary.{type}.price is already the LINE TOTAL for that guest type
  // (e.g. adult.price 40 + child.price 90 = subtotal 130) — do NOT multiply by count.
  // Flat-rate bookings instead show a single "Flat Rate" base line plus any extra-guest lines,
  // matching the old booking-details PaymentSummary (useSummaryDetails charge_flat_rate branch).
  const buildGuestLine = (type, block = {}) => {
    const count = block.count ?? 0
    const price = block.price ?? 0
    return { type, count, price, amount: price }
  }
  const guestLines =
    paymentSummary.pricing_type === "charge_flat_rate"
      ? [
          { type: "flat_rate", count: 0, price: paymentSummary.flat_rate ?? 0, amount: paymentSummary.flat_rate ?? 0 },
          ...["extra_adult", "extra_child", "extra_infant", "extra_passenger"]
            .map((type) => buildGuestLine(type, paymentSummary[type]))
            .filter((line) => line.count > 0)
        ]
      : ["adult", "child", "infant", "passenger"]
          .map((type) => buildGuestLine(type, paymentSummary[type]))
          .filter((line) => line.count > 0)

  const discountAmount = Number(item.discount_amount ?? 0)
  const tipAmount = Number(item.tip_amount ?? 0)

  // Adjusted total when "Adjust Booking Price" has been used; the top-level total_amount
  // can lag behind, so prefer the summary's computed total.
  const totalPrice = paymentSummary.total_price ?? paymentSummary.new_adjusted_price ?? item.total_amount ?? 0

  // Price adjustments made via "Adjust Booking Price"; shown as their own lines so the
  // total (and any resulting overpayment/refund) is explained. price_difference is a
  // string in the payload, and no-op ($0) entries are filtered out.
  const priceAdjustments = (paymentSummary.adjust_price_history || [])
    .map((adjustment, index) => ({
      id: adjustment.id ?? `adjustment-${index}`,
      amount: Number(adjustment.price_difference) || 0,
      reason: adjustment.reason || null
    }))
    .filter((adjustment) => adjustment.amount !== 0)

  // Single combined "Taxes & Fees" row — every fee rolled into one total, with an itemised
  // breakdown for the expandable panel. Mirrors the old booking-details PaymentSummary exactly
  // (useSummaryDetails mergeAllFeesToTax + info): the card processing fee is sourced ONLY from the
  // custom_fees "Card Processing Fee" entry, which the backend injects only when the fee is enabled
  // / passed through to the guest — so it shows and adds only in that case (we deliberately do NOT
  // read the flat card_processing_fee field, which can be present even when the guide absorbs it).
  // tax_and_fee is the platform fee; only add it when custom_fees doesn't already carry a "Platform
  // Fee" entry (avoids double-counting).
  const { combinedTaxesAndFees, taxesAndFeesBreakdown } = (() => {
    const customFeesList = paymentSummary.custom_fees || []
    const cardFeeEntry = customFeesList.find((fee) => fee.title === "Card Processing Fee")
    const regularFees = customFeesList.filter((fee) => fee.title !== "Card Processing Fee")
    const cardFeeAmount = cardFeeEntry ? Number(cardFeeEntry.price) : 0
    const hasPlatformFee = regularFees.some((fee) => fee.title === "Platform Fee")
    const platformFeeAmount = hasPlatformFee ? 0 : Number(paymentSummary.tax_and_fee ?? 0)

    const breakdown = [...regularFees, ...(cardFeeEntry ? [cardFeeEntry] : [])]
      .filter((fee) => Number(fee.price) > 0)
      .map((fee) => ({ label: fee.title, amount: Number(fee.price) }))
    if (platformFeeAmount > 0) breakdown.push({ label: "Platform Fee", amount: platformFeeAmount })

    const total = regularFees.reduce((sum, fee) => sum + Number(fee.price ?? 0), 0) + cardFeeAmount + platformFeeAmount

    return { combinedTaxesAndFees: total, taxesAndFeesBreakdown: breakdown }
  })()

  // Add-ons are part of the subtotal (subtotal = guest lines + add-ons), so they must be
  // shown as their own lines for the breakdown to reconcile to the Subtotal/Total rows.
  const addOns = (paymentSummary.add_ons || []).map((addOn, index) => ({
    id: `addon-${index}`,
    name: addOn.name || "",
    quantity: addOn.quantity ?? 1,
    unitPrice: Number(addOn.unit_price ?? 0),
    amount: Number(addOn.total_amount ?? 0)
  }))

  return {
    reservationId: item.booking_number,
    reservationNumber: item.booking_number,
    date: item.experience_date,
    // Trim so a blank/whitespace end_time normalises to null (else it survives filter(Boolean)
    // and renders a dangling "| 4:00 AM -" in the header).
    timeStart: item.start_time?.trim() || null,
    timeEnd: item.end_time?.trim() || null,
    // Marketplace bookings label a *booked* reservation "Confirmed", but must still reflect
    // canceled/completed/requested states — don't blanket-override to "Confirmed".
    status: item.marketplace_booking && item.status === "booked" ? "Confirmed" : titleCase(item.status),
    rawStatus: item.status,
    cancellationReason: item.reason || null,
    source: item.marketplace_booking ? "Marketplace" : titleCase(item.booking_source),
    bookingSource: item.booking_source,
    // "Created" is the reservation's creation timestamp (created_at, ISO), not the experience
    // date (booked_datetime). Format to match the header date style.
    created: item.created_at ? dayjs(item.created_at).format("ddd, MMM D, YYYY") : null,
    cancellationPolicy: null,

    // IDs / raw fields the reused booking-action modals need
    uuid: item.uuid,
    bookingId: item.id,
    tripUUID: trip.uuid || null,
    paymentOption: item.payment_option,
    isExpired: Boolean(item.is_expired),
    canEdit: item.can_edit ?? true,
    experienceAvatar: trip.photos || [],
    // Partial check-in/out (read-only status on the detail page). The backend check_in_state is
    // authoritative (sourced from the guest group) — the page prefers it over deriving from counts.
    checkInState,
    checkedInCount,
    checkedOutCount,
    totalGuests,
    // Per-type breakdown. Prefer backend guest_types (carries per-type checked-in/out counts).
    guestTypes: buildGuestTypes(item.guest_types, totalGuests, checkedInCount, checkedOutCount),
    // Single group row for the check-in/out modal (booking checked in/out as one unit).
    guests: buildGroupRow({
      id: item.uuid,
      name: clientName,
      guestCount: totalGuests,
      state: checkInState,
      checkedIn: checkedInCount,
      checkedOut: checkedOutCount,
      docsSigned: signedWaiverCount,
      docsTotal: waivers.length,
      documents: item.documents
    }),

    client: {
      id: client.id,
      // ClientContact/messaging keys off a numeric user_id. The detail payload exposes it as
      // client.user_id (distinct from guide_client_id) — the record has no plain `id`, so the
      // old client.id mapping sent client_ids=NaN.
      user_id: client.user_id ?? client.id,
      // The guide-client id (distinct from the user id above) is what the Edit Client
      // modal + /guide/clients/:id endpoint need.
      guideClientId: client.guide_client_id || null,
      name: clientName,
      first_name: client.first_name || null,
      last_name: client.last_name || null,
      phone: client.phone_number || null,
      email: client.email || null,
      avatarUrl: client.avatar || null
    },

    experience: {
      id: item.booking_number,
      title: trip.title || "",
      imageUrl: trip.photos?.[0]?.url || null,
      // Use the status the API already returns (e.g. "upcoming"/"completed"); title-cased for display.
      status: titleCase(item.status),
      date: item.experience_date,
      timeStart: item.start_time?.trim() || null,
      timeEnd: item.end_time?.trim() || null,
      adults: item.adult_count ?? 0,
      // Guest breakdown for the header — every type present in the booking (adult/child/infant/
      // passenger), not just adults. Prefer the payment_summary counts (same source as the
      // right-side breakdown) and fall back to the top-level {type}_count fields.
      guestBreakdown: ["adult", "child", "infant", "passenger"]
        .map((type) => ({ type, count: paymentSummary[type]?.count ?? item[`${type}_count`] ?? 0 }))
        .filter((entry) => entry.count > 0),
      // Header docs badge mirrors the listing's Waivers/Docs count. The backend sends the
      // reservation-level totals as signed_waivers_count / waivers_count; fall back to the
      // older docs_signed/docs_total and finally the booking_waivers list length.
      docsSign: item.signed_waivers_count ?? item.docs_signed ?? guestGroup?.docs_signed ?? signedWaiverCount,
      docsTotal: item.waivers_count ?? item.docs_total ?? guestGroup?.docs_total ?? waivers.length,
      total: totalPrice
    },

    waivers: waivers.map((waiver) => ({
      id: waiver.booking_waiver_id,
      fullName: clientName,
      guestEmail: client.email || null,
      phone: client.phone_number || null,
      document: `Waiver #${waiver.document_id}`,
      documentUrl: waiver.document_url || null,
      age: null,
      status: waiver.is_signed ? "Signed" : "Unsigned"
    })),

    // No questionnaire answers are returned on the detail payload yet.
    answers: [],

    payments: {
      depositLabel,
      // { label, color } — same shape as the list's payment_tag/collection_tag
      paymentTag,
      collectionTag,
      isPaidInFull,
      isCollectionFailed: collectionTag?.label === "Auto-collection failed",
      // Whether a payment link can be (re)sent for the outstanding balance. Backend-authoritative:
      // it already accounts for the balance, whether a link exists/expired, cancellation and
      // whether the client has a reachable contact method. Deliberately NOT derived from
      // collection_tag — a booking carries a single tag, so a deposit with auto-collect scheduled
      // reports the schedule and loses the fact that a link is still outstanding.
      canResendLink: Boolean(item.payment_link?.can_resend),
      // When the link was last sent, and to whom — surfaced under the Resend Link button so a
      // guide isn't resending blind. Formatted here to match isAutoCollectEnabledDateDisplay.
      paymentLinkSentAtDisplay: item.payment_link?.last_sent_at ? dayjs(item.payment_link.last_sent_at).format("MMM D, YYYY - hh:mm A") : null,
      paymentLinkSentTo: item.payment_link?.sent_to || null,
      paymentLinkChannel: item.payment_link?.channel || null,
      isAutoCollectEnabledDateDisplay: item.auto_collect_scheduled_at ? dayjs(item.auto_collect_scheduled_at).format("MMM D, YYYY - hh:mm A") : null,
      // FE-derived (experience start − 24h) so the enable-confirmation modal has a date even
      // when auto-collect is currently off and the backend hasn't scheduled one.
      collectionScheduleDisplay: buildCollectionSchedule(item.booked_datetime, item.start_time),
      isAutoCollectEnabledEnabled: Boolean(item.auto_collect_balance),
      // Whether the balance is still auto-chargeable. Gates the auto-collect toggle the same way
      // the old booking-details PaymentSummary does: the backend flips this to false once the
      // deposit's stored-card mandate is spent (e.g. after a manual full collection), so a later
      // refund reopening the balance does NOT resurrect the toggle. Honor it strictly — no default.
      isChargeable: Boolean(item.is_chargeable),
      isDepositOption: Boolean(item.payment_option?.includes("deposit")),
      outstandingBalance: item.outstanding_amount ?? 0,
      guestLines,
      addOns,
      // Aggregate of all fees not shown as their own line (platform + card processing, etc.)
      // so the breakdown reconciles to Total: subtotal + non_line_item_fees_total = total.
      // Subtotal row. When "Adjust Booking Price" negotiated the base down, the guest lines
      // still show the original per-type prices, so the Subtotal row carries the adjusted value
      // (original struck through) to reconcile the breakdown to Total. Mirrors the old page.
      subtotal: {
        amount: Number(paymentSummary.subtotal ?? paymentSummary.adjusted_subtotal ?? 0),
        isAdjusted: Boolean(paymentSummary.subtotal_adjusted),
        originalAmount: paymentSummary.original_subtotal != null ? Number(paymentSummary.original_subtotal) : null,
        reason: paymentSummary.subtotal_adjustment_reason || null
      },
      taxesAndFees: combinedTaxesAndFees,
      // Per-fee lines for the expandable Taxes & Fees breakdown ([] → render a plain row).
      taxesAndFeesBreakdown,
      discount: item.discount_applied && discountAmount > 0 ? { amount: discountAmount, code: item.discount_details?.code || null } : null,
      tip: tipAmount > 0 ? { amount: tipAmount } : null,
      priceAdjustments,
      total: totalPrice,
      remainingBalance,
      // Negative remaining = client overpaid (e.g. price adjusted down after payment) → refund due
      refundDue: remainingBalance < 0 ? Math.abs(remainingBalance) : 0,
      paidAmount: Math.max(totalPrice - remainingBalance, 0),
      adjustmentOutstandingBase: item.adjustment_fee_outstanding ?? paymentSummary.adjustment_fee_outstanding ?? 0,
      paymentStatus: item.payment_status,
      receiptUrl: item.receipt_url || null,
      invoiceUrl: item.booking_invoice || null,
      history: paymentHistory.map((entry, index) => ({
        id: `${entry.paid_on || "payment"}-${index}`,
        type: entry.type || "Payment",
        method: entry.method || "",
        cardLast4: entry.last4 || "",
        amount: entry.amount ?? 0,
        isRefund: (entry.type || "").toLowerCase() === "refund",
        paidOn: entry.paid_on ? dayjs(entry.paid_on).format("MM/DD/YYYY") : ""
      }))
    },

    // Communication history is not part of the detail payload yet.
    communications: [],
    // Card holds placed on this booking (Place/Manage Hold), mirroring the old booking
    // detail's hold_history. Empty until/unless the detail payload includes it.
    holdHistory: Array.isArray(item.hold_history) ? item.hold_history : []
  }
}

// SUB-RESOURCE MAPPERS (separate lazy-loaded endpoints)

// GET /trip_bookings/{uuid}/booking_waivers
// The backend groups waivers per guest, each carrying a `documents` array plus
// signed/unsigned document lists. The table renders one row per document, so we flatten
// each guest into a row per document and resolve the per-document signed state.
export const mapReservationWaiver = (waiver, guestIndex = 0) => {
  const documents = Array.isArray(waiver.documents) ? waiver.documents : []
  const signedDocuments = Array.isArray(waiver.signed_documents) ? waiver.signed_documents : []
  const unsignedDocuments = Array.isArray(waiver.unsigned_documents) ? waiver.unsigned_documents : []
  const guestStatus = waiver.status === "Signed" ? "Signed" : "Unsigned"

  const guest = {
    fullName: waiver.full_name || null,
    guestEmail: waiver.guest_email || null,
    phone: waiver.phone_number || null,
    age: waiver.age ?? null,
    guestType: waiver.type || null,
    documentUrl: waiver.document_url || null,
    // Notify-guest fields — the modal offers email or text and pre-fills from whichever
    // channel the waiver was originally shared by (mirrors the legacy GuestWaiverDetails).
    sharedWaiverId: waiver.shared_waiver_id ?? null,
    isPrimaryGuest: waiver.type === "Primary Guest" || !!waiver.is_primary_guest,
    shareBy: waiver.share_by || null,
    sharedEmail: waiver.shared_email || null,
    sharedPhone: waiver.shared_phone || null,
    notifyDetails: waiver.notify_details || null
  }

  // Per-document status falls back to the guest-level status when the signed/unsigned
  // document lists aren't populated.
  const documentStatus = (title) => {
    if (signedDocuments.includes(title)) return "Signed"
    if (unsignedDocuments.includes(title)) return "Unsigned"
    return guestStatus
  }

  if (documents.length === 0) {
    return [{ id: `waiver-${guestIndex}`, document: null, status: guestStatus, ...guest }]
  }

  return documents.map((title, docIndex) => ({
    id: `waiver-${guestIndex}-${docIndex}`,
    document: title,
    status: documentStatus(title),
    ...guest
  }))
}

// GET /guide/reservations/{uuid}/payment_history
export const mapPaymentHistoryEntry = (entry, index) => ({
  id: entry.id ?? `${entry.paid_on || "payment"}-${index}`,
  type: entry.type || "Payment",
  method: entry.method || "",
  cardLast4: entry.last4 || "",
  amount: entry.amount ?? 0,
  payerName: entry.payer_name || null,
  isRefund: (entry.type || "").toLowerCase() === "refund",
  paidOn: entry.paid_on ? dayjs(entry.paid_on).format("MM/DD/YYYY h:mm A") : "",
  // Refund/collection failure metadata (same field names as the old booking-details
  // PaymentHistory): a failed/pending charge carries a reason + failed/requested date.
  status: entry.status || null,
  failureReason: entry.failure_reason || null,
  failureCode: entry.failure_code || null,
  failedOn: entry.failed_at ? dayjs(entry.failed_at).format("MM/DD/YYYY h:mm A") : "",
  requestedOn: entry.requested_on ? dayjs(entry.requested_on).format("MM/DD/YYYY") : "",
  // A `refund_attempt` summary row (has `action`) is pulled out of the transaction list and
  // shown as an aggregate "N failed refund attempt(s)" banner instead of a normal row.
  action: entry.action || null,
  failureCount: entry.failure_count ?? 0,
  successCount: entry.success_count ?? 0,
  attemptedAmount: entry.attempted_amount ?? 0
})

// GET /guide/reservations/{uuid}/communication_history
// `mode` is the channel (sms/email → drives the icon); `type` is a human label ("Text"/
// "Email"); `title` is the message subject; `recipient`/`sent_at` are the contact + date.
// `status` flags failed sends; `sent_by` names the sender on manual messages (null = system).
export const mapCommunication = (comm, index) => ({
  id: comm.id ?? `comm-${index}`,
  type: comm.mode || "email",
  contactName: comm.title || null,
  label: comm.type || null,
  contactInfo: comm.recipient || "",
  date: comm.sent_at || "",
  status: comm.status || "sent",
  sentBy: comm.sent_by || null
})

// GET /guide/reservations/{uuid}/answers
export const mapAnswerTopic = (topic, index) => {
  const questions = Array.isArray(topic.questions) ? topic.questions : []
  return {
    id: topic.id ?? `answer-${index}`,
    topic: topic.topic || "",
    count: questions.length,
    questions: questions.map((qa) => ({ q: qa.question, a: qa.answer }))
  }
}

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  metadata: { ...PAGINATION_INITIAL_STATE },
  detail: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  checkIn: {
    ...LOADING_INITIAL_STATE
  },
  waivers: { ...LOADING_INITIAL_STATE, data: [], metadata: { ...PAGINATION_INITIAL_STATE } },
  paymentHistory: { ...LOADING_INITIAL_STATE, data: [], metadata: { ...PAGINATION_INITIAL_STATE } },
  communications: { ...LOADING_INITIAL_STATE, data: [], metadata: { ...PAGINATION_INITIAL_STATE } },
  answers: { ...LOADING_INITIAL_STATE, data: [] },
  paymentOption: { ...LOADING_INITIAL_STATE },
  changeHistory: { ...LOADING_INITIAL_STATE, data: [], metadata: { ...PAGINATION_INITIAL_STATE } }
}

const reservationsReducer = createReducer(initialState, {
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, action) => {
    setReceived(state)
    const incoming = Array.isArray(action.payload?.items) ? action.payload.items : []
    state.items = (action.props?.append ? state.items : []).concat(incoming.map(mapReservationItem))
    Object.assign(state.metadata, action.payload?.metadata || {})
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [requestedDetail.type]: (state) => {
    setRequested(state.detail)
  },
  [receivedDetail.type]: (state, action) => {
    setReceived(state.detail)
    state.detail.data = mapReservationDetail(action.payload)
  },
  [failedDetail.type]: (state) => {
    setFailed(state.detail)
  },
  [requestedCheckIn.type]: (state) => {
    setRequested(state.checkIn)
  },
  [receivedCheckIn.type]: (state, action) => {
    setReceived(state.checkIn)
    const updated = action.payload?.data
    if (!updated?.uuid) return
    const index = state.items.findIndex((item) => item.id === updated.uuid)
    if (index === -1) return
    if (updated.check_in_state) state.items[index].checkInState = updated.check_in_state
    if (updated.checked_in_count != null) state.items[index].checkedInCount = updated.checked_in_count
    if (updated.checked_out_count != null) state.items[index].checkedOutCount = updated.checked_out_count
    if (Array.isArray(updated.guest_types)) {
      state.items[index].guestTypes = buildGuestTypes(updated.guest_types, updated.guest_count ?? state.items[index].guestCount ?? 0, updated.checked_in_count ?? 0, updated.checked_out_count ?? 0)
    }
    // Rebuild the group row from the patched counts so the badge/modal stay in sync.
    const patched = state.items[index]
    patched.guests = buildGroupRow({
      id: updated.uuid,
      name: patched.name,
      guestCount: updated.guest_count ?? patched.guestCount ?? 0,
      state: updated.check_in_state,
      checkedIn: updated.checked_in_count ?? patched.checkedInCount ?? 0,
      checkedOut: updated.checked_out_count ?? patched.checkedOutCount ?? 0,
      docsSigned: patched.docsSign ?? 0,
      docsTotal: patched.docsTotal ?? 0
    })
  },
  [failedCheckIn.type]: (state) => {
    setFailed(state.checkIn)
  },
  [requestedWaivers.type]: (state) => {
    setRequested(state.waivers)
  },
  [receivedWaivers.type]: (state, action) => {
    setReceived(state.waivers)
    state.waivers.data = (action.payload?.data || []).flatMap((waiver, index) => mapReservationWaiver(waiver, index))
    Object.assign(state.waivers.metadata, action.payload?.metadata || {})
  },
  [failedWaivers.type]: (state) => {
    setFailed(state.waivers)
  },
  [requestedPaymentHistory.type]: (state) => {
    setRequested(state.paymentHistory)
  },
  [receivedPaymentHistory.type]: (state, action) => {
    setReceived(state.paymentHistory)
    state.paymentHistory.data = (action.payload?.data || []).map(mapPaymentHistoryEntry)
    Object.assign(state.paymentHistory.metadata, action.payload?.metadata || {})
  },
  [failedPaymentHistory.type]: (state) => {
    setFailed(state.paymentHistory)
  },
  [requestedCommunications.type]: (state) => {
    setRequested(state.communications)
  },
  [receivedCommunications.type]: (state, action) => {
    setReceived(state.communications)
    state.communications.data = (action.payload?.data || []).map(mapCommunication)
    Object.assign(state.communications.metadata, action.payload?.metadata || {})
  },
  [failedCommunications.type]: (state) => {
    setFailed(state.communications)
  },
  [requestedAnswers.type]: (state) => {
    setRequested(state.answers)
  },
  [receivedAnswers.type]: (state, action) => {
    setReceived(state.answers)
    state.answers.data = (action.payload?.data || []).map(mapAnswerTopic)
  },
  [failedAnswers.type]: (state) => {
    setFailed(state.answers)
  },
  [requestedPaymentOption.type]: (state) => {
    setRequested(state.paymentOption)
  },
  [receivedPaymentOption.type]: (state) => {
    setReceived(state.paymentOption)
  },
  [failedPaymentOption.type]: (state) => {
    setFailed(state.paymentOption)
  },
  [requestedChangeHistory.type]: (state) => {
    setRequested(state.changeHistory)
  },
  [receivedChangeHistory.type]: (state, action) => {
    setReceived(state.changeHistory)
    // "Show more" fetches the next page and appends; a fresh load (page 1) replaces.
    // Drop entries left with no displayable changes (e.g. an edit that only touched an empty field).
    const incoming = (action.payload?.data || []).map(mapBookingHistoryEntry).filter((entry) => entry.changes.length > 0)
    state.changeHistory.data = (action.props?.append ? state.changeHistory.data : []).concat(incoming)
    Object.assign(state.changeHistory.metadata, action.payload?.metadata || {})
  },
  [failedChangeHistory.type]: (state) => {
    setFailed(state.changeHistory)
  }
})

export default reservationsReducer

// PUBLIC ACTIONS

export const fetchReservations =
  (params, isAppend = false) =>
  (dispatch) => {
    const serializedParams = toSearchParams(params, null)
    return dispatch(
      api({
        url: "/guide/reservations",
        onStart: requested.type,
        onSuccess: received.type,
        onError: failed.type,
        params: serializedParams,
        props: { append: Boolean(isAppend) }
      })
    )
  }

export const fetchReservationDetail = (uuid) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}`,
      onStart: requestedDetail.type,
      onSuccess: receivedDetail.type,
      onError: failedDetail.type
    })
  )

// Auto-collect toggle on the detail page maps to a payment_option change. Wired with
// loading watchers so the confirmation modal's Proceed button reflects Redux loading.
export const updateReservationPaymentOption = (uuid, paymentOption) => (dispatch) =>
  dispatch(
    api({
      url: `guide/bookings/${uuid}/update_payment_option`,
      method: "PATCH",
      onStart: requestedPaymentOption.type,
      onSuccess: receivedPaymentOption.type,
      onError: failedPaymentOption.type,
      data: { trip_booking: { payment_option: paymentOption } }
    })
  )

export const patchCheckIn = (uuid, checkInState) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/check_in`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: { state: checkInState }
    })
  )

// Partial check-in for private bookings: set the absolute checked-in count per guest type.
// `guestTypes` is [{ key, checkedInCount }]. We also send the aggregate `checked_in_count`
// so the backend keeps working until the per-type payload is consumed.
// override_documents bypasses the required-documents check (Incomplete Documents → Override).
export const checkInGuests = (uuid, guestTypes = [], { override = false } = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/check_in`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: {
        guest_types: guestTypes.map((type) => ({ key: type.key, checked_in_count: type.checkedInCount })),
        checked_in_count: guestTypes.reduce((sum, type) => sum + (type.checkedInCount ?? 0), 0),
        override_documents: override
      }
    })
  )

// Partial check-out: set the absolute checked-out count per guest type (0..that type's
// checked-in count). Aggregate `checked_out_count` is sent alongside for backward-compat.
export const checkOutGuests = (uuid, guestTypes = []) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/check_out`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: {
        guest_types: guestTypes.map((type) => ({ key: type.key, checked_out_count: type.checkedOutCount })),
        checked_out_count: guestTypes.reduce((sum, type) => sum + (type.checkedOutCount ?? 0), 0)
      }
    })
  )

// ---------------------------------------------------------------------------
// Group check-in/out for private bookings. The whole booking is checked in/out as
// one unit, so we send a single group state transition (not per-guest ids or
// per-type counts). The backend echoes the updated booking (reconciled in
// receivedCheckIn).
// ---------------------------------------------------------------------------

// Check the whole group IN. `override` bypasses the required-documents gate.
export const checkInGroup = (uuid, { override = false } = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/check_in`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: { state: "in", override_documents: override }
    })
  )

// Check the whole group OUT.
export const checkOutGroup = (uuid) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/check_out`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: { state: "out" }
    })
  )

// Reset the whole group back to pending (undo a check-in, or reset after check-out).
export const resetGroup = (uuid) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/reset_check_in`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: { state: "pending" }
    })
  )

// Resend a specific unsigned waiver/document for the booking.
export const resendGroupDocument = (uuid, { documentId } = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/waivers/resend`,
      method: "POST",
      data: { document_id: documentId }
    })
  )

// Resend the payment link for a single reservation's outstanding balance.
// Reservation-scoped rather than the upcoming_experiences group route, which 404s once an
// experience is past/completed/canceled; this one accepts any non-draft booking. No body;
// resolves with { payment_link } and shows the API `message` — which names the recipient — as a
// success toast.
export const resendPaymentLink = (uuid) =>
  api({
    url: `/guide/reservations/${uuid}/payment_link/resend`,
    method: "POST",
    showSuccessNotification: true
  })

// Lazy-loaded detail sub-resources
export const fetchReservationWaivers = (uuid, params = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/trip_bookings/${uuid}/booking_waivers`,
      params,
      onStart: requestedWaivers.type,
      onSuccess: receivedWaivers.type,
      onError: failedWaivers.type
    })
  )

export const fetchReservationPaymentHistory = (uuid, params = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/payment_history`,
      params,
      onStart: requestedPaymentHistory.type,
      onSuccess: receivedPaymentHistory.type,
      onError: failedPaymentHistory.type
    })
  )

// The dedicated endpoint defaults to per_page=3, so pass a large page size explicitly —
// the panel renders one scrollable, searchable list with no pagination controls.
export const fetchReservationCommunications = (uuid, params = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/communication_history`,
      params: { per_page: 50, ...params },
      onStart: requestedCommunications.type,
      onSuccess: receivedCommunications.type,
      onError: failedCommunications.type
    })
  )

// GET /guide/bookings/{id}/change-history — booking edit log (id or uuid both work). Its own
// paginated micro-endpoint (like payment/communication history): fetched on detail open, and
// "Show more" refetches the next page with append=true so pages accumulate in the panel.
export const fetchReservationChangeHistory =
  (bookingId, params = {}, { append = false } = {}) =>
  (dispatch) =>
    dispatch(
      api({
        url: `/guide/bookings/${bookingId}/change-history`,
        params: { page: 1, per_page: 10, ...params },
        onStart: requestedChangeHistory.type,
        onSuccess: receivedChangeHistory.type,
        onError: failedChangeHistory.type,
        props: { append }
      })
    )

export const fetchReservationAnswers = (uuid) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/answers`,
      onStart: requestedAnswers.type,
      onSuccess: receivedAnswers.type,
      onError: failedAnswers.type
    })
  )
